import { create } from "zustand";

interface ViewContext {
  shortContext: string; // Auto-injected via runtime onNew, ~50 tokens
  longContext?: string; // On-demand via tool, ~500 tokens
}

interface ViewContextStore {
  contexts: Record<string, ViewContext>; // key -> contexts

  setViewContext: (key: string, shortContext: string, longContext?: string) => void;

  getShortContext: (key: string) => string | undefined;

  getLongContext: (key: string) => string | undefined;

  getAllShortContexts: () => string; // Combines all short contexts for runtime onNew

  getAllLongContexts: () => string; // Combines all long contexts for tool response

  clearViewContext: (key: string) => void;
}

export const useViewContextStore = create<ViewContextStore>((set, get) => ({
  contexts: {},

  setViewContext: (key, shortContext, longContext) =>
    set((state) => ({
      contexts: {
        ...state.contexts,
        [key]: { shortContext, longContext },
      },
    })),

  getShortContext: (key) => get().contexts[key]?.shortContext,

  getLongContext: (key) => get().contexts[key]?.longContext,

  getAllShortContexts: () => {
    const contexts = get().contexts;
    return Object.entries(contexts)
      .map(([_, ctx]) => ctx.shortContext)
      .filter(Boolean)
      .join("\n\n");
  },

  getAllLongContexts: () => {
    const contexts = get().contexts;
    const entries = Object.entries(contexts)
      .filter(([_, ctx]) => ctx.longContext)
      .map(([key, ctx]) => `## ${key}\n\n${ctx.longContext}`);

    if (entries.length === 0) {
      return "No detailed context available for current views.";
    }

    return entries.join("\n\n---\n\n");
  },

  clearViewContext: (key) =>
    set((state) => {
      const newContexts = { ...state.contexts };
      delete newContexts[key];
      return { contexts: newContexts };
    }),
}));
